import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { STATS, HONOURS } from "@/data/content";

function Counter({ to, suffix }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let raf;
    const duration = 1400;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(eased * to));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to]);

  return (
    <span ref={ref}>
      {val}
      {suffix}
    </span>
  );
}

export default function Stats() {
  return (
    <section id="impact" className="relative py-24 md:py-32 bg-[#050505]" data-testid="impact-section">
      <div className="container mx-auto px-6 md:px-12">
        <div className="flex items-center gap-3 mb-14">
          <span className="font-mono text-xs tracking-[0.25em] uppercase text-volt">03 / Impact</span>
          <span className="h-px flex-1 bg-neutral-800" />
        </div>

        {/* Stat grid — control room */}
        <div className="grid grid-cols-2 lg:grid-cols-4 border-t border-l border-neutral-800">
          {STATS.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="border-b border-r border-neutral-800 p-6 md:p-10 group hover:bg-[#0d0d0d] transition-colors"
              data-testid={`stat-card-${i}`}
            >
              <div className="font-heading text-6xl md:text-7xl leading-none text-white group-hover:text-volt transition-colors">
                <Counter to={s.value} suffix={s.suffix} />
              </div>
              <p className="mt-4 font-body font-semibold text-sm uppercase tracking-wide text-neutral-200">
                {s.label}
              </p>
              <p className="mt-1 font-mono text-[11px] uppercase tracking-wide text-neutral-500">
                {s.context}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Honours */}
        <div className="mt-16">
          <h3 className="font-heading uppercase text-2xl text-white mb-6">Silverware & Honours</h3>
          <div className="space-y-0">
            {HONOURS.map((h, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="grid grid-cols-[70px_1fr] md:grid-cols-[100px_1fr_auto] gap-4 items-center py-5 border-b border-neutral-800 group"
                data-testid={`honour-row-${i}`}
              >
                <span className="font-mono text-volt text-sm">{h.year}</span>
                <span className="font-heading uppercase text-lg md:text-2xl text-white group-hover:text-volt transition-colors">
                  {h.title}
                </span>
                <span className="hidden md:block font-mono text-xs uppercase tracking-wide text-neutral-500">
                  {h.team}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
